/**
 * TKF design tokens.
 *
 * Modern-minimal, three-colour system: green, white, black. There is no yellow
 * or sand in the palette — every warm tone was removed. Green is the only
 * chromatic colour and is spent sparingly: one accent per view, never a wash.
 */
export const theme = {
  colors: {
    /**
     * Green. 500 is the accent used for emphasis on light surfaces; 700 is the
     * deep forest brand green used for solid fills and dark surfaces.
     */
    primary: {
      50: '#f2f8f4',
      100: '#e1f0e7',
      200: '#bedfcb',
      300: '#8fc7a6',
      400: '#54a87a',
      500: '#2e8b57', // Accent green
      600: '#226942',
      700: '#1a4731', // Deep forest (brand)
      800: '#133524',
      900: '#0c2016',
    },
    /** Muted green-greys for quiet surfaces, badges and supporting marks. */
    secondary: {
      50: '#f6f9f7',
      100: '#eaf1ec',
      200: '#d6e3da',
      300: '#b6cbbe',
      400: '#8faa9a',
      500: '#6b8a78',
      600: '#526d5d',
      700: '#3e5347',
      800: '#2c3b33',
      900: '#1d2721',
    },
    /**
     * Neutrals run white → ink with a barely perceptible green cast, so white
     * surfaces sit next to green without going cold.
     */
    neutral: {
      50: '#fafbfa',
      100: '#f4f5f3', // Muted section surface
      200: '#e9ebe8', // Hairline / border
      300: '#d8dbd6',
      400: '#b4b8b2',
      500: '#8a8f88',
      600: '#63685f', // Secondary text
      700: '#40443e',
      800: '#22251f',
      900: '#101210', // Ink
    },
    success: '#2e8b57',
    warning: '#63685f',
    error: '#b42318',
    info: '#1a4731',
    text: '#101210',
    textMuted: '#63685f',
    border: '#e9ebe8',
    surface: '#f4f5f3',
    background: '#ffffff',
    ink: '#101210',
    white: '#ffffff',
  },
  typography: {
    fontFamily: {
      /** Geometric display face — tight, confident, used for every heading. */
      heading: '"Plus Jakarta Sans", "Helvetica Neue", Arial, sans-serif',
      /** Neutral text face, optimised for long-form legibility. */
      body: '"Inter", "Helvetica Neue", Arial, sans-serif',
    },
    /** Display sizes are fluid; body sizes are fixed so text stays readable. */
    fontSize: {
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': 'clamp(1.5rem, 1.35rem + 0.75vw, 1.875rem)',
      '3xl': 'clamp(1.875rem, 1.55rem + 1.6vw, 2.75rem)',
      '4xl': 'clamp(2.25rem, 1.7rem + 2.75vw, 3.75rem)',
      '5xl': 'clamp(2.75rem, 1.85rem + 4.5vw, 5rem)',
      '6xl': 'clamp(3.25rem, 1.9rem + 6.75vw, 6.5rem)',
    },
    fontWeight: {
      thin: 100,
      extralight: 200,
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
      black: 900,
    },
    lineHeight: {
      display: 1.04,
      tight: 1.12,
      snug: 1.35,
      normal: 1.6,
      relaxed: 1.75,
    },
    /** Negative tracking on display type, generous tracking on small caps. */
    letterSpacing: {
      display: '-0.035em',
      tight: '-0.02em',
      normal: '0',
      wide: '0.02em',
      eyebrow: '0.14em',
    },
  },
  spacing: {
    0: '0',
    1: '0.25rem',
    2: '0.5rem',
    3: '0.75rem',
    4: '1rem',
    5: '1.25rem',
    6: '1.5rem',
    8: '2rem',
    10: '2.5rem',
    12: '3rem',
    14: '3.5rem',
    16: '4rem',
    20: '5rem',
    24: '6rem',
    28: '7rem',
    32: '8rem',
    40: '10rem',
  },
  /** Vertical rhythm for full-bleed sections, fluid between mobile and desktop. */
  sectionPadding: {
    sm: 'clamp(3rem, 2rem + 5vw, 5rem)',
    md: 'clamp(4rem, 2.5rem + 7vw, 7rem)',
    lg: 'clamp(5rem, 3rem + 9vw, 9rem)',
  },
  layout: {
    maxWidth: '1200px',
    narrowWidth: '760px',
  },
  /** Soft, tinted, low-contrast. Elevation is a whisper, not a drop shadow. */
  shadows: {
    none: 'none',
    sm: '0 1px 2px 0 rgba(16, 18, 16, 0.04)',
    DEFAULT: '0 1px 3px 0 rgba(16, 18, 16, 0.06)',
    md: '0 6px 16px -6px rgba(16, 18, 16, 0.10)',
    lg: '0 16px 32px -12px rgba(16, 18, 16, 0.12)',
    xl: '0 28px 56px -20px rgba(16, 18, 16, 0.16)',
  },
  /**
   * Softly rounded rather than sharp. `minimal` is the workhorse radius used by
   * buttons, inputs and small surfaces across the site.
   */
  borderRadius: {
    none: '0',
    sharp: '0',
    minimal: '10px',
    sm: '6px',
    DEFAULT: '10px',
    md: '12px',
    lg: '16px',
    xl: '20px',
    '2xl': '28px',
    full: '9999px',
  },
  animation: {
    duration: {
      fast: '160ms',
      normal: '260ms',
      slow: '480ms',
    },
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.16, 1, 0.3, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    },
  },
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
} as const;

export type Theme = typeof theme;
